import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "../lib/api";
import { usePaged, fetchAll, useDebounced, LoadMore } from "../lib/paged";
import { apiFail } from "../lib/p2";
import { useAuth } from "../lib/auth";
import { Btn, Card, Field, Input, PageTitle, Select, Tag } from "../components/kit";
import { NoAccess, Loading } from "../components/common";
import { fmtDateTime } from "../lib/format";

function GrantForm({ u, onDone }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ type: "learning_all", course_id: "", expires_at: "", reason: "" });
  const [courses, setCourses] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pickType = (e) => { setF({ ...f, type: e.target.value }); if (e.target.value === "learning_course" && !courses) fetchAll("/learning/catalogue").then(setCourses); };
  const save = () => api.post("/admin/grants", { user_id: u.id, type: f.type, course_id: f.course_id || null, reason: f.reason, expires_at: f.expires_at ? new Date(f.expires_at).toISOString() : null })
    .then(() => { toast.success(t("common.saved")); setF({ ...f, reason: "" }); onDone(); }).catch(apiFail(t));
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="grant-form">
      <Field label={t("adm.type")} htmlFor="g-type"><Select id="g-type" data-testid="grant-type-select" value={f.type} onChange={pickType}>
        {["learning_all", "learning_course", "practice"].map((k) => <option key={k} value={k}>{t(`adm.t_${k}`)}</option>)}</Select></Field>
      {f.type === "learning_course" && <Field label={t("adm.course")} htmlFor="g-course"><Select id="g-course" data-testid="grant-course-select" value={f.course_id} onChange={set("course_id")}>
        <option value="">{t("adm.choose_course")}</option>{(courses || []).map((c) => <option key={c.id} value={c.id}>{c.number} · {c.title?.en}</option>)}</Select></Field>}
      <Field label={t("adm.expires")} htmlFor="g-exp" hint={t("adm.expires_hint")}><Input id="g-exp" type="date" data-testid="grant-expiry-input" value={f.expires_at} onChange={set("expires_at")} /></Field>
      <Field label={t("adm.reason")} htmlFor="g-reason" required><Input id="g-reason" data-testid="grant-reason-input" value={f.reason} onChange={set("reason")} /></Field>
      <div className="sm:col-span-2"><Btn testid="grant-submit-btn" disabled={!f.reason.trim()} onClick={save}>{t("adm.grant")}</Btn></div>
    </div>
  );
}

function GrantRow({ g, onDone }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const revoke = () => api.post(`/admin/grants/${g.id}/revoke`, { reason }).then(() => { toast.success(t("common.saved")); onDone(); }).catch(apiFail(t));
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3 text-sm" data-testid={`grant-row-${g.id}`}>
      <b>{t(`adm.t_${g.type}`)}{g.course_number ? ` · ${g.course_number}` : ""}</b>
      <Tag data-testid={`grant-status-${g.id}`}>{g.effective ? t("adm.active") : g.status === "revoked" ? t("adm.revoked") : t("adm.expired")}</Tag>
      <span className="text-muted-foreground">{g.expires_at ? t("adm.until", { d: fmtDateTime(g.expires_at) }) : t("adm.no_expiry")} · {g.reason}</span>
      {g.effective && <span className="ml-auto flex flex-wrap gap-2">
        <Input aria-label={t("adm.revoke_reason")} placeholder={t("adm.revoke_reason")} data-testid={`revoke-reason-${g.id}`} value={reason} onChange={(e) => setReason(e.target.value)} className="w-48" />
        <Btn variant="danger" testid={`revoke-btn-${g.id}`} disabled={!reason.trim()} onClick={revoke}>{t("adm.revoke")}</Btn></span>}
    </li>
  );
}

function UserPanel({ u }) {
  const { t } = useTranslation();
  const gs = usePaged(`/admin/grants`, { user_id: u.id });
  return (
    <Card className="space-y-4" data-testid="admin-user-panel">
      <h2 className="text-lg font-bold break-words">{u.display_name || u.email} <span className="text-sm font-normal text-muted-foreground">{u.email}</span></h2>
      <GrantForm u={u} onDone={gs.reload} />
      <h3 className="font-bold">{t("adm.grants")}</h3>
      {!gs.items ? <Loading /> : gs.items.length ? <ul className="space-y-2" data-testid="grant-list">{gs.items.map((g) => <GrantRow key={g.id} g={g} onDone={gs.reload} />)}</ul>
        : <p className="text-sm text-muted-foreground" data-testid="no-grants">{t("adm.none")}</p>}
      <LoadMore shown={(gs.items || []).length} total={gs.total} onMore={gs.more} loading={gs.loading} testid="grants" />
    </Card>
  );
}

function AuditList() {
  const { t } = useTranslation();
  const a = usePaged("/admin/audit", {}, 20);
  return (
    <Card data-testid="admin-audit"><h2 className="mb-3 text-lg font-bold">{t("adm.audit")}</h2>
      <ul className="space-y-1 text-sm">{(a.items || []).map((x) => <li key={x.id} data-testid="admin-audit-row">{fmtDateTime(x.at)} · {x.actor_email} · {t(`adm.a_${x.action}`)} · {x.user_email || ""} · {x.new?.type || x.old?.type ? t(`adm.t_${x.new?.type || x.old?.type}`) : ""} · {x.reason || ""}</li>)}</ul>
      <LoadMore shown={(a.items || []).length} total={a.total} onMore={a.more} loading={a.loading} testid="admin-audit" /></Card>
  );
}

function SettingsCard() {
  const { t } = useTranslation();
  const [v, setV] = useState("");
  const save = () => api.put("/admin/settings", { support_email: v }).then(() => toast.success(t("common.saved"))).catch(apiFail(t));
  return (
    <Card className="flex flex-wrap items-end gap-3"><Field label={t("adm.support_email")} htmlFor="s-email"><Input id="s-email" type="email" data-testid="support-email-input" value={v} onChange={(e) => setV(e.target.value)} /></Field>
      <Btn variant="outline" testid="support-email-save" onClick={save}>{t("adm.save")}</Btn></Card>
  );
}

export default function AdminAccess() {
  const { t } = useTranslation();
  const { me } = useAuth();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const dq = useDebounced(q);
  const users = usePaged(me.user.platform_admin ? "/admin/users" : null, { q: dq || undefined });
  if (!me.user.platform_admin) return <NoAccess />;
  return (
    <div className="mx-auto max-w-4xl space-y-5" data-testid="admin-access-page">
      <PageTitle title={t("adm.title")} sub={t("adm.sub")} />
      <Card className="space-y-3">
        <Field label={t("adm.search")} htmlFor="u-q"><Input id="u-q" data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} /></Field>
        <ul className="space-y-1" data-testid="admin-user-list">{(users.items || []).map((u) => (
          <li key={u.id}><button data-testid={`admin-user-${u.email}`} onClick={() => setSel(u)} className="flex min-h-[44px] w-full flex-wrap items-center gap-2 rounded-xl border bg-white px-3 text-left text-sm hover:bg-muted">
            <b className="break-all">{u.email}</b><span className="text-muted-foreground">{u.display_name}</span>
            <span className="ml-auto text-xs">{u.access.exempt ? t("adm.exempt") : [u.access.learning_all && t("adm.t_learning_all"), u.access.courses.length > 0 && t("adm.n_courses", { n: u.access.courses.length }), u.access.practice && t("adm.t_practice")].filter(Boolean).join(" · ") || t("adm.no_access")}</span>
          </button></li>))}</ul>
        <LoadMore shown={(users.items || []).length} total={users.total} onMore={users.more} loading={users.loading} testid="admin-users" />
      </Card>
      {sel && <UserPanel key={sel.id} u={sel} />}
      <SettingsCard />
      <AuditList />
    </div>
  );
}
